import { QR } from '@/components';
import { ORG } from '@/config/org';
import { titledName } from '@/lib/format';
import type { Certificate, Locale, Profile } from '@/types/domain';

export interface CertSignature {
  name: string;
  role: string;
}

export interface CertPayload {
  id: string;
  course: string;
  th: string;
  host: string;
  /** Two signature blocks: the course instructor + the org certifying authority. */
  signatures: CertSignature[];
  name: string;
  /** Language the holder name is rendered in — drives the Thai (Sarabun) font. */
  locale: Locale;
  issued: string;
  expires: string;
}

/**
 * Build a printable certificate payload from a wallet record + holder.
 * `locale` picks the holder's name language — Thai renders "{prefix} {firstTh}
 * {lastTh}", English "{enPrefix} {firstEn} {lastEn}, {suffix}" — with the
 * prefix/suffix enforced per language by titledName().
 */
export function certPayload(c: Certificate, holder: Profile, locale: Locale = 'en'): CertPayload {
  // A course may have multiple instructors; they're stored newline-delimited and
  // each gets its own signature block. (A certifying authority can be re-added here.)
  const signatures: CertSignature[] = c.instructor
    .split('\n')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => ({ name, role: 'Course Instructor' }));
  return {
    id: c.id,
    course: c.course,
    th: c.th,
    host: c.host,
    signatures,
    name: titledName(holder, locale),
    locale,
    issued: c.issued,
    expires: c.expires,
  };
}

/** The public URL anyone can scan to verify a certificate. */
export function verifyUrl(id: string): string {
  const base = import.meta.env.VITE_APP_URL ?? 'https://warmpaper.club';
  return `${base}/verify/${id}`;
}

/* ── Treatment A · Formal (seal, double border, IDs) ── */
export function CertFormal({ d, w = 720 }: { d: CertPayload; w?: number }) {
  const h = w / 1.414;
  const s = w / 720;
  return (
    <div
      style={{
        width: w,
        height: h,
        background: '#fbfaf7',
        position: 'relative',
        fontFamily: 'var(--font)',
        color: '#1c1b19',
        overflow: 'hidden',
        boxShadow: 'var(--sh-lg)',
        flex: 'none',
      }}
    >
      <div style={{ position: 'absolute', inset: 18 * s, border: `${1.5 * s}px solid var(--sage)` }} />
      <div style={{ position: 'absolute', inset: 24 * s, border: `${0.75 * s}px solid var(--sage)`, opacity: 0.5 }} />

      <div style={{ position: 'absolute', inset: 0, padding: `${54 * s}px ${64 * s}px`, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11 * s, letterSpacing: '.22em', color: 'var(--sage-deep)' }}>{ORG.hospital.en.toUpperCase()}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9.5 * s, letterSpacing: '.18em', color: '#908a7e', marginTop: 2 * s }}>EMERGENCY DEPARTMENT</div>
            <div className="th" style={{ fontSize: 12 * s, color: '#908a7e', marginTop: 2 * s }}>{ORG.owner.th} · {ORG.hospital.th}</div>
          </div>
          <div style={{ width: 56 * s, height: 56 * s, position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `${2 * s}px solid var(--sage)` }} />
            <div style={{ position: 'absolute', inset: 6 * s, borderRadius: '50%', border: `${1 * s}px solid var(--sage)`, opacity: 0.6 }} />
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--sage)', fontFamily: 'var(--mono)', fontSize: 9 * s, letterSpacing: '.1em' }}>ED</div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12 * s, letterSpacing: '.3em', color: 'var(--sage-deep)' }}>CERTIFICATE OF COMPLETION</div>
          <div className="th" style={{ fontSize: 13 * s, color: '#908a7e', marginTop: 4 * s }}>ใบรับรองการอบรม</div>
          <div style={{ fontSize: 13 * s, color: '#908a7e', margin: `${22 * s}px 0 ${8 * s}px` }}>This certifies that</div>
          <div className={d.locale === 'th' ? 'th' : undefined} style={{ fontSize: 34 * s, fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1.1 }}>{d.name}</div>
          <div style={{ width: 220 * s, height: 1, background: '#d9d2c4', margin: `${14 * s}px auto` }} />
          <div style={{ fontSize: 13 * s, color: '#908a7e' }}>has successfully completed</div>
          <div style={{ fontSize: 19 * s, fontWeight: 600, marginTop: 6 * s }}>{d.course}</div>
          <div className="th" style={{ fontSize: 14 * s, color: '#56524a', marginTop: 2 * s }}>{d.th}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'flex-end', gap: 16 * s }}>
          {/* both signatures stacked on the left */}
          <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12 * s }}>
            {d.signatures.map((sg, i) => (
              <div key={i}>
                <div style={{ fontSize: 13 * s, fontWeight: 500, borderBottom: '1px solid #d9d2c4', paddingBottom: 3 * s, marginBottom: 4 * s, minWidth: 150 * s, maxWidth: 210 * s }}>{sg.name}</div>
                <div style={{ fontSize: 10 * s, color: '#908a7e', fontFamily: 'var(--mono)', letterSpacing: '.05em' }}>{sg.role.toUpperCase()}</div>
              </div>
            ))}
          </div>
          {/* QR centered on the certificate */}
          <div style={{ justifySelf: 'center', display: 'flex', justifyContent: 'center' }}>
            <QR value={verifyUrl(d.id)} size={58 * s} />
          </div>
          {/* right column — original layout: certificate ID + dates */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10.5 * s, color: '#908a7e', fontFamily: 'var(--mono)', letterSpacing: '.05em' }}>CERTIFICATE ID</div>
            <div style={{ fontSize: 13 * s, fontWeight: 500, fontFamily: 'var(--mono)' }}>{d.id}</div>
            <div style={{ fontSize: 11.5 * s, color: '#908a7e', marginTop: 6 * s }}>Issued {d.issued}</div>
            <div style={{ fontSize: 11.5 * s, color: '#908a7e', marginTop: 2 * s }}>Expires {d.expires}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Treatment B · Minimal (typographic, restrained) ── */
export function CertMinimal({ d, w = 720 }: { d: CertPayload; w?: number }) {
  const h = w / 1.414;
  const s = w / 720;
  const Field = ({ l, v }: { l: string; v: string }) => (
    <div>
      <div style={{ fontSize: 9.5 * s, fontFamily: 'var(--mono)', color: '#908a7e', letterSpacing: '.1em' }}>{l}</div>
      <div style={{ fontSize: 13 * s, fontWeight: 500, marginTop: 2 * s }}>{v}</div>
    </div>
  );
  return (
    <div
      style={{
        width: w,
        height: h,
        background: '#fbfaf7',
        position: 'relative',
        fontFamily: 'var(--font)',
        color: '#1c1b19',
        overflow: 'hidden',
        boxShadow: 'var(--sh-lg)',
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 8 * s, background: 'var(--sage)' }} />
      <div style={{ padding: `${56 * s}px ${64 * s}px ${48 * s}px ${72 * s}px`, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11 * s, letterSpacing: '.2em', color: 'var(--sage-deep)' }}>{ORG.hospital.en.toUpperCase()}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9 * s, letterSpacing: '.18em', color: '#908a7e', marginTop: 2 * s }}>EMERGENCY DEPARTMENT · {d.host}</div>
          </div>
        </div>

        <div style={{ marginTop: 'auto' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12 * s, letterSpacing: '.28em', color: '#908a7e' }}>CERTIFICATE OF COMPLETION</div>
          <div style={{ fontSize: 15 * s, color: '#908a7e', margin: `${28 * s}px 0 ${6 * s}px` }}>Awarded to</div>
          <div className={d.locale === 'th' ? 'th' : undefined} style={{ fontSize: 44 * s, fontWeight: 600, letterSpacing: '-.03em', lineHeight: 1 }}>{d.name}</div>
          <div style={{ fontSize: 15 * s, color: '#56524a', marginTop: 22 * s, maxWidth: 440 * s, lineHeight: 1.5 }}>
            for completing <strong style={{ fontWeight: 600 }}>{d.course}</strong>
            <span className="th" style={{ color: '#908a7e' }}> · {d.th}</span>, including all required lessons, assessment and attendance.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 40 * s, paddingTop: 22 * s, borderTop: '1px solid #e4ded3' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Field l="ISSUED" v={d.issued} />
              <Field l="EXPIRES" v={d.expires} />
              {/* Instructors stack vertically so a 2nd signatory sits directly below the 1st. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 * s }}>
                {d.signatures.map((sg, i) => (
                  <Field key={i} l={sg.role.toUpperCase()} v={sg.name} />
                ))}
              </div>
            </div>
            <div style={{ fontSize: 11 * s, fontFamily: 'var(--mono)', color: '#908a7e', letterSpacing: '.05em' }}>ID {d.id}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <QR value={verifyUrl(d.id)} size={62 * s} />
            <span style={{ fontSize: 9 * s, fontFamily: 'var(--mono)', color: '#908a7e', letterSpacing: '.1em' }}>SCAN TO VERIFY</span>
          </div>
        </div>
      </div>
    </div>
  );
}
